import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { PlanoAlimentarService } from '../../../../../services/plano-alimentar/plano-alimentar.service';
import { PlanoAlimentar } from '../../../../../models/PlanoAlimentar';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-adicionar-plano',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    MatListModule,
    MatCheckbox,
    NgxMaskDirective,
  ],
  templateUrl: './adicionar-plano.html',
  styleUrl: './adicionar-plano.scss',
  providers: [
    provideNgxMask({
      dropSpecialCharacters: false,
    }),
  ],
})
export class AdicionarPlano implements OnInit {
  caracteristicasPlano: string[] = [
    'Emagrecimento',
    'Hipercalórico',
    'Normocalórico',
    'Hiperproteico',
    'Hipossódico',
    'Hipoglicídico',
    'Hipolipídico',
    'Vegetariano/Vegano',
  ];

  caracteristicasSelecionadas: string[] = [];
  condicoesEspecificasSelecionada: boolean = false;
  condicoesClinicasEspecificas: string = '';
  dataInicial: string = '';
  dataFinal: string = '';
  formularioValido: boolean = false;

  msgErro: string = '';
  planosAlimentaresDaPessoa: PlanoAlimentar[] = [];
  datasTocadas: boolean = false;
  condicoesEspeciaisTocadas: boolean = false;
  caracteristicasTocadas: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AdicionarPlano>,
    public planoAlimentarService: PlanoAlimentarService,
    private route: ActivatedRoute,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  async ngOnInit(): Promise<void> {
    this.planosAlimentaresDaPessoa =
      await this.buscaPlanosAlimentaresDaPessoa();
  }

  async buscaPlanosAlimentaresDaPessoa(): Promise<PlanoAlimentar[]> {
    let id;
    const partesUrl = window.location.href.split('/');
    const idUrl = partesUrl[partesUrl.length - 1];
    if (idUrl && !id) {
      id = idUrl;
    }

    if (!id) {
      return [];
    }

    const res = await this.planoAlimentarService.listarPlanosAlimentares(
      Number(id)
    );
    return res;
  }

  private verificarSobreposicaoComPlanosExistentes(
    dataInicio: Date,
    dataFim: Date
  ): boolean {
    if (
      !this.planosAlimentaresDaPessoa ||
      this.planosAlimentaresDaPessoa.length === 0
    ) {
      return false;
    }

    for (const plano of this.planosAlimentaresDaPessoa) {
      if (!plano.periodo?.inicio || !plano.periodo?.fim) {
        continue;
      }

      const inicioExistente = this.converterParaDate(plano.periodo.inicio);
      const fimExistente = this.converterParaDate(plano.periodo.fim);

      const haSobreposicao =
        (dataInicio >= inicioExistente && dataInicio <= fimExistente) || // Início dentro de um período existente
        (dataFim >= inicioExistente && dataFim <= fimExistente) || // Fim dentro de um período existente
        (dataInicio <= inicioExistente && dataFim >= fimExistente); // Novo período engloba um período existente

      if (haSobreposicao) {
        return true;
      }
    }

    return false;
  }

  onCheckboxChange(item: string, checked: boolean): void {
    if (checked && !this.caracteristicasSelecionadas.includes(item)) {
      this.caracteristicasSelecionadas.push(item);
    } else if (!checked) {
      this.caracteristicasSelecionadas =
        this.caracteristicasSelecionadas.filter((c) => c !== item);
    }
    this.validarFormulario();
  }

  onCondicoesCheckboxChange(checked: boolean): void {
    this.condicoesEspecificasSelecionada = checked;
    if (!checked) this.condicoesClinicasEspecificas = '';
    this.validarFormulario();
  }

  private validarDataString(dataStr: string): boolean {
    const regexData = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regexData.test(dataStr)) return false;

    const [diaStr, mesStr, anoStr] = dataStr.split('/');
    const dia = parseInt(diaStr, 10);
    const mes = parseInt(mesStr, 10);
    const ano = parseInt(anoStr, 10);

    const anoAtual = new Date().getFullYear();
    const anoMax = anoAtual + 2;

    if (mes < 1 || mes > 12) return false;

    const maxDiasPorMes: Record<number, number> = {
      1: 31,
      2: 29,
      3: 31,
      4: 30,
      5: 31,
      6: 30,
      7: 31,
      8: 31,
      9: 30,
      10: 31,
      11: 30,
      12: 31,
    };
    if (dia < 1 || dia > maxDiasPorMes[mes]) return false;

    if (ano < anoAtual || ano > anoMax) return false;

    return true;
  }

  private converterParaDate(dataStr: string): Date {
    const [dia, mes, ano] = dataStr.split('/').map(Number);
    return new Date(ano, mes - 1, dia);
  }

  validarFormulario(): void {
    this.formularioValido = true;
    this.msgErro = '';

    let erroEncontrado = '';

    const dataInicialValida = this.validarDataString(this.dataInicial);
    const dataFinalValida = this.validarDataString(this.dataFinal);

    if (!dataInicialValida || !dataFinalValida) {
      erroEncontrado =
        '*As datas início e fim devem estar preenchidas corretamente.';
    }

    if (!erroEncontrado) {
      const dtInicial = this.converterParaDate(this.dataInicial);
      const dtFinal = this.converterParaDate(this.dataFinal);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      if (dtInicial < hoje) {
        erroEncontrado =
          '*A data de início não pode ser anterior ao dia de hoje.';
      } else if (dtFinal < dtInicial) {
        erroEncontrado =
          '*A data final não pode ser anterior à data de início.';
      } else {
        const haSobreposicao = this.verificarSobreposicaoComPlanosExistentes(
          dtInicial,
          dtFinal
        );
        if (haSobreposicao) {
          erroEncontrado =
            '*O período selecionado conflita com um plano alimentar existente. Escolha datas que não se sobreponham a outros planos.';
        }
      }
    }

    if (erroEncontrado && this.datasTocadas) {
      this.formularioValido = false;
      this.msgErro = erroEncontrado;
      return;
    }

    let erroCondicoesEspeciais = '';
    const condicoesEspecificasTexto = this.condicoesClinicasEspecificas.trim();

    if (this.condicoesEspecificasSelecionada) {
      if (
        condicoesEspecificasTexto.length < 15 ||
        condicoesEspecificasTexto.length > 300
      ) {
        erroCondicoesEspeciais =
          '*Condições específicas devem conter entre 15 e 300 caracteres.';
      }
    }

    if (erroCondicoesEspeciais && this.condicoesEspeciaisTocadas) {
      this.formularioValido = false;
      this.msgErro = erroCondicoesEspeciais;
      return;
    }

    const possuiCaracteristicas = this.caracteristicasSelecionadas.length > 0;

    const condicoesValidas =
      !this.condicoesEspecificasSelecionada ||
      (this.condicoesEspecificasSelecionada && !erroCondicoesEspeciais);

    const temConteudoPrincipal =
      possuiCaracteristicas ||
      (this.condicoesEspecificasSelecionada && condicoesValidas);

    let erroConteudoPrincipal = '';

    if (!temConteudoPrincipal) {
      erroConteudoPrincipal =
        '*Selecione pelo menos uma característica ou preencha as condições específicas corretamente.';
    }

    if (
      erroConteudoPrincipal &&
      (this.caracteristicasTocadas || this.condicoesEspeciaisTocadas)
    ) {
      this.formularioValido = false;
      this.msgErro = erroConteudoPrincipal;
      return;
    }

    if (erroEncontrado || erroCondicoesEspeciais) {
      this.formularioValido = false;
    } else {
      this.formularioValido = temConteudoPrincipal;
    }

    if (this.formularioValido) {
      this.msgErro = '';
    }
  }

  onConfirm(): void {
    this.validarFormulario();
    if (!this.formularioValido) return;

    const resultado = {
      caracteristicas: this.caracteristicasSelecionadas,
      condicoesClinicasEspecificas: this.condicoesEspecificasSelecionada
        ? this.condicoesClinicasEspecificas.trim()
        : '',
      periodo: {
        inicio: this.dataInicial,
        fim: this.dataFinal,
      },
    };

    this.dialogRef.close(resultado);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
